import {
  Button,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { BadgeModel } from 'types'
import dayjs from 'dayjs'

export default function BadgeAssignPage() {
  const { profileId } = useParams<{ profileId: string }>()
  const navigate = useNavigate()
  const [allBadges, setAllBadges] = useState<BadgeModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('token')
    axios
      .get(`${base_url}/badge/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setAllBadges(res.data)
      })
      .catch((err) => {
        console.error('Failed to load badges:', err)
        alert('Impossible de charger la liste des badges.')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleSubmit = () => {
    if (!selectedBadgeId) {
      alert('Veuillez sélectionner un badge.')
      return
    }

    setIsSaving(true)
    const token = localStorage.getItem('token')
    // The API requires the unlock date. We'll use the current time.
    const payload = {
      badgeId: selectedBadgeId,
      unlockedDate: dayjs().unix(),
    }

    axios
      .post(`${base_url}/profile/${profileId}/badge`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Badge assigné avec succès !')
        navigate(`/profile/${profileId}`)
      })
      .catch((error) => {
        console.error('Failed to assign badge:', error)
        alert(
          "L'assignation a échoué: " +
            (error.response?.data?.message || 'Erreur inconnue')
        )
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement des badges...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Assigner un badge au profil`}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Sélectionner un badge à assigner</Typography>
        <Paper sx={{ width: '100%', maxHeight: '60vh', overflow: 'auto' }}>
          <List>
            {allBadges.length > 0 ? (
              allBadges.map((badge, index) => (
                <Box key={badge.id}>
                  <ListItem
                    button
                    selected={selectedBadgeId === badge.id}
                    onClick={() => setSelectedBadgeId(badge.id!)}
                  >
                    <ListItemText
                      primary={badge.name}
                      secondary={badge.description}
                    />
                  </ListItem>
                  {index < allBadges.length - 1 && <Divider />}
                </Box>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="Aucun badge n'a été trouvé." />
              </ListItem>
            )}
          </List>
        </Paper>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving || !selectedBadgeId}
          sx={{ mt: 2, minWidth: '200px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Confirmer et Assigner'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
